import { createContext, useContext, useState, useEffect } from "react";

import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext(null);

const syncSupabaseUser = async (session) => {
  if (!session?.access_token) {
    return null;
  }

  sessionStorage.setItem("nv_token", session.access_token);

  const provider =
    session.user?.app_metadata?.provider ||
    session.user?.identities?.[0]?.provider ||
    "unknown";

  const { data } = await api.post("/auth/supabase-sync", {
    provider,
  });

  if (!data?.user) {
    throw new Error("Unable to sync Supabase account");
  }

  return data.user;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welcomeUser, setWelcomeUser] = useState(null);

  useEffect(() => {
    let mounted = true;

    const restoreSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (session) {
          const syncedUser = await syncSupabaseUser(session);

          if (mounted) {
            setUser(syncedUser);
          }
        } else {
          const token = sessionStorage.getItem("nv_token");

          if (token) {
            try {
              const response = await api.get("/auth/me");

              if (mounted) {
                setUser(response.data);
              }
            } catch {
              sessionStorage.removeItem("nv_token");

              if (mounted) {
                setUser(null);
              }
            }
          }
        }
      } catch (error) {
        console.error("Authentication restore failed:", error);

        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    restoreSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (session) {
        try {
          const syncedUser = await syncSupabaseUser(session);

          if (mounted) {
            setUser(syncedUser);
          }
        } catch (error) {
          console.error("Supabase authentication sync failed:", error);
          sessionStorage.removeItem("nv_token");

          if (mounted) {
            setUser(null);
          }
        }
      } else if (event === "SIGNED_OUT") {
        sessionStorage.removeItem("nv_token");

        if (mounted) {
          setUser(null);
          setWelcomeUser(null);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });

    sessionStorage.setItem("nv_token", data.token);
    setUser(data.user);
    setWelcomeUser(data.user);

    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", {
      name,
      email,
      password,
    });

    sessionStorage.setItem("nv_token", data.token);
    setUser(data.user);

    return data.user;
  };

  const registerVendor = async (name, email, password) => {
    const { data } = await api.post("/vendor/register", {
      name,
      email,
      password,
    });

    sessionStorage.setItem("nv_token", data.token);
    setUser(data.user);

    return data.user;
  };

  const loginWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/login`,
      },
    });

    if (error) {
      throw error;
    }
  };

  const loginWithFacebook = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "facebook",
      options: {
        redirectTo: `${window.location.origin}/login`,
      },
    });

    if (error) {
      throw error;
    }
  };

  const requestWhatsAppSignupOtp = async (name, email, phone) => {
    const { data } = await api.post(
      "/auth/customer/whatsapp-signup/request-otp",
      { name, email, phone }
    );
    return data;
  };

  const verifyWhatsAppSignupOtp = async (name, email, phone, otp) => {
    const { data } = await api.post(
      "/auth/customer/whatsapp-signup/verify-otp",
      { name, email, phone, otp }
    );
    sessionStorage.setItem("nv_token", data.token);
    setUser(data.user);
    setWelcomeUser(data.user);
    return data.user;
  };

  const requestWhatsAppLoginOtp = async (phone) => {
    const { data } = await api.post(
      "/auth/customer/whatsapp-login/request-otp",
      { phone }
    );
    return data;
  };

  const verifyWhatsAppLoginOtp = async (phone, otp) => {
    const { data } = await api.post(
      "/auth/customer/whatsapp-login/verify-otp",
      { phone, otp }
    );
    sessionStorage.setItem("nv_token", data.token);
    setUser(data.user);
    setWelcomeUser(data.user);
    return data.user;
  };

  const requestWhatsAppOtp = async (phone) => {
    const { data } = await api.post(
      "/auth/customer/whatsapp/request-otp",
      { phone }
    );
    return data;
  };

  const verifyWhatsAppOtp = async (phone, otp) => {
    const { data } = await api.post(
      "/auth/customer/whatsapp/verify-otp",
      { phone, otp }
    );
    setUser((current) => (
      current
        ? {
            ...current,
            whatsapp_phone: data.whatsapp_phone,
            whatsapp_verified: data.whatsapp_verified,
            whatsapp_transactional_opt_in:
              data.whatsapp_transactional_opt_in,
          }
        : current
    ));
    return data;
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (error) {
      console.error("Supabase sign-out failed:", error);
    }

    sessionStorage.removeItem("nv_token");
    setUser(null);
    setWelcomeUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        registerVendor,
        loginWithGoogle,
        loginWithFacebook,
        requestWhatsAppSignupOtp,
        verifyWhatsAppSignupOtp,
        requestWhatsAppLoginOtp,
        verifyWhatsAppLoginOtp,
        requestWhatsAppOtp,
        verifyWhatsAppOtp,
        logout,
        welcomeUser,
        clearWelcome: () => setWelcomeUser(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
